/**
 * Typed error class for API failures. Every fetch through @shared/api/client
 * throws an ApiError on non-2xx; callers can switch on `kind` instead of
 * inspecting raw status codes.
 */
export type ApiErrorKind =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'validation'
  | 'conflict'
  | 'rate_limit'
  | 'server'
  | 'unknown';

export interface ApiErrorBody {
  /**
   * NestJS returns a plain string for most HttpExceptions, but the
   * ValidationPipe (class-validator) returns an ARRAY of per-field messages:
   *   { message: ["email must be an email", "password must be longer …"] }
   * Both shapes are preserved verbatim; use `fieldErrors()` to read the array.
   */
  message?: string | string[];
  code?: string;
  details?: unknown;
}

/** A single field-level validation failure parsed out of a 400 body. */
export type FieldError = { field: string; message: string };

/** Collapse NestJS's string-or-array `message` into one human sentence. */
function flattenMessage(message: string | string[] | undefined): string | undefined {
  if (Array.isArray(message)) return message.length > 0 ? message.join('. ') : undefined;
  return message;
}

export class ApiError extends Error {
  public readonly kind: ApiErrorKind;
  public readonly status: number;
  public readonly body: ApiErrorBody | null;
  public readonly url: string;
  /**
   * Seconds until the caller may retry, when the server said so. conmap sends
   * this on 429 as SUFFIXED headers (`Retry-After-auth`,
   * `X-RateLimit-Reset-<group>`) rather than a bare `Retry-After`, so it is
   * resolved in the client and passed in here. `null` when unknown - never
   * guess a countdown we were not given.
   */
  public readonly retryAfterSeconds: number | null;

  constructor(args: {
    kind: ApiErrorKind;
    status: number;
    url: string;
    body: ApiErrorBody | null;
    message?: string;
    retryAfterSeconds?: number | null;
  }) {
    super(
      args.message ?? flattenMessage(args.body?.message) ?? `API ${args.kind} (${args.status})`
    );
    this.name = 'ApiError';
    this.kind = args.kind;
    this.status = args.status;
    this.body = args.body;
    this.url = args.url;
    this.retryAfterSeconds = args.retryAfterSeconds ?? null;
  }

  /**
   * Per-field validation failures, parsed from a class-validator 400 body.
   * class-validator formats each entry as "<property> <constraint text>", so
   * the field name is the leading token. Returns [] for every other kind.
   */
  public fieldErrors(): FieldError[] {
    if (this.kind !== 'validation') return [];
    const raw = this.body?.message;
    if (!Array.isArray(raw)) return [];
    return raw.flatMap<FieldError>((entry) => {
      if (typeof entry !== 'string' || entry.trim() === '') return [];
      const trimmed = entry.trim();
      const [head, ...rest] = trimmed.split(/\s+/);
      // Treat the leading token as a field name only when it really looks like
      // a DTO property: camelCase/snake_case starting lowercase (`email`,
      // `password`, `spec.targets[0]`), with a constraint clause after it.
      // class-validator prose ("Only organization email addresses are
      // allowed.") starts with a capital and must stay form-level, otherwise we
      // would invent a field called "Only".
      const looksLikeField = rest.length > 0 && /^[a-z_][\w.[\]]*$/.test(head ?? '');
      if (!looksLikeField) return [{ field: '', message: trimmed }];
      return [{ field: head as string, message: rest.join(' ') }];
    });
  }

  static fromStatus(
    status: number,
    url: string,
    body: ApiErrorBody | null,
    retryAfterSeconds?: number | null
  ): ApiError {
    const kind: ApiErrorKind =
      status === 401
        ? 'unauthorized'
        : status === 403
          ? 'forbidden'
          : status === 404
            ? 'not_found'
            : status === 409
              ? 'conflict'
              : // NestJS's ValidationPipe returns 400, not 422 - conmap's
                // /auth/register returns 400 with a message[] of field errors.
                // Both are treated as validation so the UI can mark fields.
                status === 400 || status === 422
                ? 'validation'
                : status === 429
                  ? 'rate_limit'
                  : status >= 500
                    ? 'server'
                    : 'unknown';
    return new ApiError({ kind, status, url, body, retryAfterSeconds });
  }
}
